import React, { useEffect, useState } from 'react';
import { Modal, Checkbox, Form, Label } from 'semantic-ui-react';
import { categoryApi } from 'areas/settings/category/api/categoryApi';
import { SurveystructureComplianceRead } from 'store/generated/admin/category';
import { v4 as uuidv4 } from 'uuid';
import { StyledButton, StyledCancelButton, StyledSaveButton } from 'common/components/button/StyledButton';

interface AddCategoriesModalProps {
  open: boolean;
  organizationId: number;
  onClose: () => void;
  onCategoriesUpdated: (newCategories: any[]) => void;
  existingCategoryNames: string[];
}


const OrganizationSurveyStructureAddCategories: React.FC<AddCategoriesModalProps> = ({
    open,
    onClose,
    organizationId,
    onCategoriesUpdated,
    existingCategoryNames
}) => {
    const [categories, setCategories] = useState<SurveystructureComplianceRead[]>([]);
    const [selectedCategoryIds, setSelectedCategoryIds] = useState<Set<string>>(new Set());
    const allSelected = categories.length > 0 && categories.every(c => selectedCategoryIds.has(String(c.id)));
    const someSelected = categories.some(c => selectedCategoryIds.has(String(c.id)));

    const { data: allCategories = [],
        isLoading: isLoadingCategories,
        error: loadError,
    } = categoryApi.useApiAdminV1CategoryListQuery({ status: 1 });


useEffect(() => {
  if (!open || !allCategories?.length) return;

  const lowerExistingNames = existingCategoryNames.map(n => n.toLowerCase());

  const relevantCategories = allCategories
    .filter(cat =>
      (Number(cat.priority) === 3 ||
        ((Number(cat.priority) === 1 || Number(cat.priority) === 2) &&
          cat.organization === organizationId)) &&
      !lowerExistingNames.includes((cat.name ?? '').toLowerCase())
    )
    .sort((a, b) => {
      if (Number(a.priority) !== Number(b.priority)) {
        return Number(a.priority) - Number(b.priority);
      }
      return (a.name ?? '').localeCompare(b.name ?? '');
    });

  setCategories(relevantCategories);
  setSelectedCategoryIds(new Set(relevantCategories.map(c => String(c.id))));
}, [open, allCategories, organizationId, existingCategoryNames]);


    const toggleCategory = (id: string) => {
        setSelectedCategoryIds(prev => {
            const newSet = new Set(prev);
            newSet.has(id) ? newSet.delete(id) : newSet.add(id);
            return newSet;
        });
    };

    const handleSave = () => {
        const selected = categories.filter(c => selectedCategoryIds.has(String(c.id)));

        const selectedCategories = selected.map(c => ({
            id: uuidv4(),
            name: c.name,
            input: c.default_prompt,
            options: c.categories.map((cat: { id: any; name: any; color: any; description: any; fr: any; }) => ({
                id: String(cat.id),
                name: cat.name,
                color: cat.color,
                description: cat.description,
                fr: cat.fr
            })),
            priority: c.priority === 1 ? "primary" : c.priority === 2 ? "secondary" : "tertiary",
            subtitle: c.name,
            visibility: c.default_visibility,
            question: c.default_question,
            subfactors: [],
        }));

        onCategoriesUpdated(selectedCategories);
        onClose();
    };

    return (
        <Modal open={open} onClose={onClose} size='small'>
            <Modal.Header>Add Categories</Modal.Header>
            <Modal.Content scrolling>
                <Form>
                    {/* Header Row */}
                    <Form.Field
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '6px 12px',
                            backgroundColor: '#f2f2f2',
                            margin: 0,
                            fontWeight: 'bold',
                            borderBottom: '1px solid #ccc',
                            borderTop: '1px solid #ccc',
                        }}
                    >
                        <div style={{ flex: '0 0 30px' }}>
                            <Checkbox
                                checked={categories.length > 0 && categories.every(c => selectedCategoryIds.has(String(c.id)))}
                                indeterminate={
                                    categories.some(c => selectedCategoryIds.has(String(c.id))) &&
                                    !categories.every(c => selectedCategoryIds.has(String(c.id)))
                                }
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const allSelected = categories.length > 0 && categories.every(c => selectedCategoryIds.has(String(c.id)));
                                    if (allSelected) {
                                        setSelectedCategoryIds(new Set());
                                    } else {
                                        setSelectedCategoryIds(new Set(categories.map(c => String(c.id))));
                                    }
                                }}
                            />
                        </div>
                        <div style={{ flex: '1', paddingLeft: 8 }}>
                            Category Name
                        </div>
                        <div style={{ flex: '0 0 200px' }}>
                            Priority
                        </div>
                    </Form.Field>

                    {/* Category Rows */}
                    {categories.map(c => {
                        const isSelected = selectedCategoryIds.has(String(c.id));

                        return (
                            <Form.Field
                                key={c.id}
                                onClick={() => toggleCategory(String(c.id))}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '6px 12px',
                                    margin: 0,
                                    cursor: 'pointer',
                                    borderRadius: 0,
                                    backgroundColor: isSelected ? '#e0f3ff' : 'transparent',
                                    transition: 'background-color 0.2s ease',
                                }}
                                onMouseEnter={(e: { currentTarget: HTMLDivElement }) => {
                                    (e.currentTarget as HTMLDivElement).style.backgroundColor = isSelected
                                        ? '#d4ecfb'
                                        : '#f9f9f9';
                                }}
                                onMouseLeave={(e: { currentTarget: HTMLDivElement }) => {
                                    (e.currentTarget as HTMLDivElement).style.backgroundColor = isSelected
                                        ? '#e0f3ff'
                                        : 'transparent';
                                }}
                            >
                                <div
                                    style={{
                                        flex: '0 0 30px',
                                        pointerEvents: 'none',
                                    }}
                                >
                                    <Checkbox
                                        checked={isSelected}
                                        readOnly
                                    />
                                </div>
                                <div style={{ flex: '1', fontWeight: 'bold', paddingLeft: 8 }}>{c.name}</div>
                                <div style={{ flex: '0 0 200px' }}>
                                    <Label color={c.priority === 1 ? 'blue' : c.priority === 2 ? 'yellow' : 'grey'}>
                                        {c.priority === 1 ? 'Primary' : c.priority === 2 ? 'Custom' : 'nuLogic'}
                                    </Label>
                                </div>
                            </Form.Field>
                        );
                    })}
                </Form>

            </Modal.Content>
            <Modal.Actions>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                    <StyledCancelButton onClick={onClose} />
                    <StyledButton color='blue' onClick={handleSave} label="OK" />
                </div>
            </Modal.Actions>
        </Modal>

    );
};

export default OrganizationSurveyStructureAddCategories;


